import {
  mapAgentCreateResponse,
  mapAgentDetailResponse,
  mapAgentKnowledgeBases,
  mapAgentResponse,
  mapAgentTemplates,
  mapAgentTools,
  mapAgentUpdateResponse,
  mapDropdownOptions,
  mapLLMResponse,
  mapVoiceResponse,
} from '@/mappers/agent.mapper';
import { defaultPaginatedResponse, mapPaginatedResponse } from '@/mappers/common.mapper';

import type { AgentCreateFormValues, AgentUpdateFormValues } from '@/schemas/agent.schema';

import apiClient from '@/lib/api-client';
import {
  buildDataExtractionJsonSchema,
  parseDataExtractionJsonSchema,
} from '@/lib/data-extraction';
import { raiseAxiosErrorOrDefault } from '@/lib/error';
import { buildSentimentJsonSchema } from '@/lib/sentiment';
import { buildQueryParams } from '@/lib/utils';

import type {
  Agent,
  AgentDetailResponse,
  AgentFilterParams,
  AgentKnowledgeBase,
  AgentTemplate,
  AgentTool,
  AgentUpdateResponseSchema,
  Voice,
} from '@/types/agent.type';
import type { DropdownOption, PaginatedResponse } from '@/types/common.type';

export const fetchAllAgents = async (
  params?: AgentFilterParams
): Promise<PaginatedResponse<Agent>> => {
  try {
    return await apiClient.get<PaginatedResponse<Agent>>(
      '/agents',
      mapPaginatedResponse(mapAgentResponse),
      {
        params: buildQueryParams(params, {
          ...(params?.filters && { status: params.filters.status }),
        }),
      }
    );
  } catch {
    return defaultPaginatedResponse<Agent>();
  }
};

export const createAgent = async (data: AgentCreateFormValues): Promise<string> => {
  try {
    const { template_id, ...rest } = data;
    const payload = {
      ...rest,
      // The UI uses 'blank'/string ids for the radio group; the API expects an
      // integer template id (or null when starting from blank).
      template_id: template_id && template_id !== 'blank' ? Number(template_id) : null,
    };
    const response = await apiClient.post<string>('/agents', payload, mapAgentCreateResponse);

    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to create agent');
  }
};

export const deleteAgentById = async (agentId: number): Promise<void> => {
  try {
    await apiClient.delete(`/agent/${agentId}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to delete agent');
  }
};

export const fetchAgentById = async (agentId: string): Promise<AgentDetailResponse> => {
  try {
    const response = await apiClient.get<AgentDetailResponse>(
      `/agent/${agentId}`,
      mapAgentDetailResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch agent');
  }
};

export const updateAgentById = async (
  agentId: number,
  data: Partial<AgentUpdateFormValues>
): Promise<AgentUpdateResponseSchema> => {
  try {
    const parsedDataExtractionSchema =
      data.data_extraction_schema_mode === 'json'
        ? parseDataExtractionJsonSchema(data.data_extraction_json_schema).schema
        : undefined;
    const hasDataExtraction =
      Boolean(data.data_extraction_prompt?.trim()) ||
      (data.data_extraction_schema_mode === 'json'
        ? Boolean(data.data_extraction_json_schema?.trim())
        : (data.data_extraction_properties?.length ?? 0) > 0);

    const payload = {
      agent: {
        agent_name: data.agent_name,
        temperature: data.temperature,
        voice_id: data.voice_id,
      },
      system_prompt: {
        prompt: data.system_prompt,
        variables: data.prompt_variables,
      },
      call_settings: {
        ring_duration: data.ring_duration,
        max_call_duration: data.max_call_duration,
        end_call_on_silence: data.end_call_silence,
        is_call_recording_enabled: data.is_call_recording_enabled,
        ...(data.webhook_config.url && {
          webhook_config: {
            url: data.webhook_config.url,
            headers: data.webhook_config.headers,
            query_params: data.webhook_config.query_params,
            timeout_in_milliseconds: data.webhook_config.timeout,
            events: data.webhook_config.events,
          },
        }),
      },
      post_call: [
        {
          prompt: data.summary_prompt,
          type: 'Summary',
          variables: data.summary_prompt_variables,
        },
        ...(hasDataExtraction
          ? [
              {
                prompt: data.data_extraction_prompt,
                type: 'Extraction',
                variables: data.data_extraction_prompt_variables,
                json_schema:
                  parsedDataExtractionSchema ||
                  buildDataExtractionJsonSchema(data.data_extraction_properties, {
                    rootIsArray: data.data_extraction_root_is_array,
                  }),
              },
            ]
          : []),
        {
          prompt: data.sentiment_prompt,
          type: 'Sentiment',
          variables: data.sentiment_prompt_variables,
          // The structured-output schema constrains `label` to the user-defined tags; the
          // backend requires a json_schema for structured prompts.
          json_schema: buildSentimentJsonSchema(data.sentiment_tags),
        },
      ],
    };

    const response = await apiClient.patch<AgentUpdateResponseSchema>(
      `/agent/${agentId}`,
      payload,
      mapAgentUpdateResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to update agent:');
  }
};

export const fetchAllVoices = async (): Promise<Voice[]> => {
  try {
    const response = await apiClient.get<Voice[]>('/agent/voices', mapVoiceResponse);
    return response;
  } catch {
    throw new Error('Failed to fetch voices');
  }
};

export const fetchLLMModels = async (): Promise<DropdownOption[]> => {
  try {
    const response = await apiClient.get<DropdownOption[]>('/agent/llm-models', mapLLMResponse);
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch LLM models');
  }
};

export const updateAgentStatus = async (agentId: number, status: string): Promise<void> => {
  try {
    await apiClient.patch<void>(`/agent/${agentId}/status`, { status }, null);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to update agent status');
  }
};

export const fetchUnassociatedAgents = async (): Promise<DropdownOption[]> => {
  try {
    const response = await apiClient.get<DropdownOption[]>(
      '/agents/unassociated',
      mapDropdownOptions
    );
    return response;
  } catch {
    return [];
  }
};

/**
 * Fetch knowledge bases for an agent.
 * @param linked - true = only linked, false = only unlinked, undefined = all with is_linked flag
 */
export const fetchAgentKnowledgeBases = async (
  agentId: number,
  linked?: boolean
): Promise<AgentKnowledgeBase[]> => {
  try {
    return await apiClient.get<AgentKnowledgeBase[]>(
      `/agent/${agentId}/knowledge-bases`,
      mapAgentKnowledgeBases,
      {
        params: {
          ...(linked !== undefined && { linked }),
        },
      }
    );
  } catch {
    return [];
  }
};

/**
 * Link a knowledge base to an agent
 */
export const linkKnowledgeBaseToAgent = async (agentId: number, kbId: number): Promise<void> => {
  try {
    await apiClient.patch(`/agent/${agentId}`, { knowledge_base_ids: [kbId] }, null);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to link knowledge base to agent');
  }
};

/**
 * Unlink a knowledge base from an agent
 */
export const unlinkKnowledgeBaseFromAgent = async (
  agentId: number,
  kbId: number
): Promise<void> => {
  try {
    await apiClient.delete(`/agent/${agentId}/knowledge-bases/${kbId}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to unlink knowledge base from agent');
  }
};

/**
 * Fetch tools for an agent.
 * @param linked - true = only linked, false = only unlinked, undefined = all with is_linked flag
 */
export const fetchAgentTools = async (agentId: number, linked?: boolean): Promise<AgentTool[]> => {
  try {
    return await apiClient.get<AgentTool[]>(`/agent/${agentId}/tools`, mapAgentTools, {
      params: {
        ...(linked !== undefined && { linked }),
      },
    });
  } catch {
    return [];
  }
};

/**
 * Link a tool to an agent
 */
export const linkToolToAgent = async (agentId: number, toolId: string): Promise<void> => {
  try {
    await apiClient.patch(`/agent/${agentId}`, { tool_ids: [toolId] }, null);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to link tool to agent');
  }
};

/**
 * Unlink a tool from an agent
 */
export const unlinkToolFromAgent = async (agentId: number, toolId: string): Promise<void> => {
  try {
    await apiClient.delete(`/agent/${agentId}/tools/${toolId}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to unlink tool from agent');
  }
};

export const fetchAgentTemplates = async (): Promise<AgentTemplate[]> => {
  try {
    return await apiClient.get<AgentTemplate[]>('/agent/templates', mapAgentTemplates);
  } catch {
    return [];
  }
};
